import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Download } from "lucide-react";
import { useShop } from "../lib/hooks";
import { listTokenHistory } from "../lib/api";
import BrandHeader from "../components/BrandHeader";
import { FullPageLoader, ShopMissing } from "../components/States";

const RANGES = [
  { label: "Today", days: 0 },
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
];

export default function HistoryPage() {
  const { slug } = useParams();
  const { shop, loading: shopLoading } = useShop(slug);
  const [rangeDays, setRangeDays] = useState(7);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!shop?.id) return;
    setLoading(true);
    listTokenHistory(shop.id, { days: rangeDays })
      .then(setRows)
      .finally(() => setLoading(false));
  }, [shop?.id, rangeDays]);

  const filtered = useMemo(() => {
    if (rangeDays !== 0) return rows;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return rows.filter((r) => new Date(r.created_at) >= today);
  }, [rows, rangeDays]);

  if (shopLoading) return <FullPageLoader label="Opening history" />;
  if (!shop) return <ShopMissing slug={slug} />;

  function exportCsv() {
    const headers = ["token", "name", "whatsapp", "service", "status", "created_at"];
    const lines = [headers.join(",")];
    for (const r of filtered) {
      lines.push(
        [r.token_number, csv(r.customer_name), csv(r.whatsapp), csv(r.service_name), r.status, r.created_at].join(",")
      );
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `aibox-${shop.slug}-history.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="min-h-screen bg-void text-white">
      <BrandHeader
        shop={shop}
        right={
          <Link
            to={`/s/${slug}/admin`}
            className="text-white/50 hover:text-gold text-sm inline-flex items-center gap-1"
            data-testid="history-back"
          >
            <ArrowLeft className="w-4 h-4" /> Admin
          </Link>
        }
      />

      <main className="px-5 md:px-10 pb-16 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="font-numbers text-xs tracking-[0.4em] uppercase text-gold">
            History & Audit
          </div>
          <h1 className="font-heading text-3xl md:text-4xl mt-1">All tokens</h1>
        </motion.div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          {RANGES.map((r) => (
            <button
              key={r.label}
              onClick={() => setRangeDays(r.days)}
              data-testid={`range-${r.days}`}
              className={`text-xs font-numbers tracking-[0.3em] uppercase rounded-full px-4 py-2 border transition-all ${
                rangeDays === r.days
                  ? "border-gold text-gold bg-gold/5"
                  : "border-white/15 text-white/60 hover:border-white/40"
              }`}
            >
              {r.label}
            </button>
          ))}
          <div className="flex-1" />
          <button
            onClick={exportCsv}
            data-testid="export-csv"
            className="btn-ghost text-xs inline-flex items-center gap-1.5 py-2 px-4"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>

        <div className="mt-6 rounded-2xl bg-charcoal border border-white/10 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-white/40 text-sm">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-white/40 text-sm">
              No tokens in this range.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-numbers tracking-[0.3em] uppercase text-white/40 border-b border-white/10">
                    <th className="px-4 py-3">Token</th>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">WhatsApp</th>
                    <th className="px-4 py-3">Service</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Time</th>
                  </tr>
                </thead>
                <tbody data-testid="history-table-body">
                  {filtered.map((r) => (
                    <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-3 font-numbers text-gold tabular-nums">{r.token_number}</td>
                      <td className="px-4 py-3">{r.customer_name}</td>
                      <td className="px-4 py-3 font-numbers text-white/60 text-xs">{r.whatsapp}</td>
                      <td className="px-4 py-3 text-white/80">{r.service_name || "—"}</td>
                      <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                      <td className="px-4 py-3 font-numbers text-white/50 text-xs">
                        {new Date(r.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    waiting: "text-white/60 border-white/20",
    serving: "text-gold border-gold/40",
    completed: "text-emerald-400 border-emerald-400/30",
    skipped: "text-red-400 border-red-400/30",
  };
  return (
    <span
      className={`inline-block text-[10px] font-numbers tracking-[0.25em] uppercase border rounded-full px-2 py-0.5 ${map[status] || map.waiting}`}
    >
      {status}
    </span>
  );
}

function csv(v) {
  if (v == null) return "";
  const s = String(v).replace(/"/g, '""');
  if (s.includes(",") || s.includes('"') || s.includes("\n")) return `"${s}"`;
  return s;
}
